import { SearchIcon } from "lucide-react";
import * as React from "react";
import { useForm } from "react-hook-form";
import { Button } from "@/components/ui/button";

interface FormInputs {
  query: string;
}

interface Props {
  setQuery: (query: string) => void;
  query?: string;
  isFetching: boolean;
  placeholder?: string;
  ariaLabel?: string;
  searchRole?: boolean;
}

export const SnapshotGridViewSearch: React.FC<Props> = ({
  isFetching,
  query,
  setQuery,
  placeholder = "Enter SnapShot Name",
  ariaLabel,
  searchRole = false,
}) => {
  const methods = useForm<FormInputs>({ defaultValues: { query: query ?? "" } });
  const { getValues, reset } = methods;

  React.useEffect(() => {
    const nextQuery = query ?? "";
    if (getValues("query") !== nextQuery) {
      reset({ query: nextQuery });
    }
  }, [getValues, query, reset]);

  const handleReset = () => {
    setQuery("");
    reset({ query: "" });
  };

  const onSearch = async (payload: FormInputs) => {
    if (!payload.query) {
      return;
    }
    setQuery(payload.query);
  };

  return (
    <form
      role={searchRole ? "search" : undefined}
      onSubmit={methods.handleSubmit(onSearch)}
      className="flex items-start"
    >
      <div className="relative mx-1 flex-grow">
        <input
          type="text"
          {...methods.register("query")}
          aria-label={ariaLabel}
          placeholder={placeholder}
          className="w-full rounded-md border border-gray-400 py-2 pl-10 pr-4 text-sm focus:border-blue-500 focus:outline-none"
          style={{ height: 36 }}
        />
        <SearchIcon className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
      </div>
      <Button
        variant="second-white"
        type="submit"
        size="sm"
        disabled={!!query && isFetching}
        onClick={methods.handleSubmit(onSearch)}
      >
        {query && isFetching ? "Searching..." : "Search"}
      </Button>
      {query && (
        <Button type="button" size="sm" className="ml-2" onClick={handleReset} variant="second-white">
          Clear
        </Button>
      )}
    </form>
  );
};
